import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff, Copy } from 'lucide-react';
import api from '../api';
import { useIsMobile } from '../hooks/useResponsive';

const TIPI = ['VPS','Database','CDN','DNS','Email','Storage','CI/CD','Dominio','Altro'];
const TIPI_COLOR = {
  VPS:'var(--cyan)', Database:'var(--purple)', CDN:'var(--yellow)', DNS:'var(--green)',
  Email:'var(--yellow)', Storage:'#3b82f6', 'CI/CD':'#ec4899', Dominio:'var(--text-muted)', Altro:'var(--text-dim)',
};

const EMPTY = { nome:'', tipo:'VPS', host:'', porta:'', username:'', password:'', note:'', stato:'attivo', tags:[] };

function CopyBtn({ value }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button onClick={() => { navigator.clipboard.writeText(value); setDone(true); setTimeout(()=>setDone(false),1500); }}
      style={{ background:'none', border:'none', cursor:'pointer', color: done ? 'var(--green)' : 'var(--text-dim)', padding:'2px 4px', display:'flex' }} title="Copia">
      <Copy size={12}/>
    </button>
  );
}

export default function Servers() {
  const [servers, setServers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showPwd, setShowPwd] = useState({});
  const [filtroTipo, setFiltroTipo] = useState('');

  const load = () => api.get('/servers').then(r => setServers(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditItem(null); setModalOpen(true); };
  const openEdit = (s) => { setForm({ ...s, porta: s.porta || '', tags: s.tags || [] }); setEditItem(s); setModalOpen(true); };

  const submit = async () => {
    if (!form.nome.trim()) return;
    const payload = { ...form, porta: form.porta ? parseInt(form.porta) : null };
    if (editItem) await api.put(`/servers/${editItem.id}`, payload);
    else await api.post('/servers', payload);
    setModalOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm('Eliminare questo server?')) return;
    await api.delete(`/servers/${id}`); load();
  };

  const togPwd = (id) => setShowPwd(p => ({ ...p, [id]: !p[id] }));

  const filtered = filtroTipo ? servers.filter(s => s.tipo === filtroTipo) : servers;
  const isMobile = useIsMobile();

  // Raggruppa per tipo
  const gruppi = TIPI.map(t => ({ tipo: t, items: filtered.filter(s => s.tipo === t) })).filter(g => g.items.length > 0);
  const altri = filtered.filter(s => !TIPI.includes(s.tipo));
  if (altri.length) gruppi.push({ tipo: 'Altro', items: altri });

  return (
    <div className="eden-page">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, margin:'0 0 4px', color:'var(--text)' }}>Servers</h1>
          <p style={{ fontSize:13, color:'var(--text-muted)', margin:0 }}>Credenziali e accessi — visibile solo admin</p>
        </div>
        <button onClick={openNew} className="eden-btn-primary" style={{ fontSize:12 }}>
          <Plus size={14}/> Nuovo server
        </button>
      </div>

      {/* Filtro tipo */}
      <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap' }}>
        <button onClick={() => setFiltroTipo('')}
          style={{ fontSize:11, padding:'4px 10px', borderRadius:6, border:'1px solid var(--border)', background: !filtroTipo ? 'var(--cyan-dim)' : 'transparent', color: !filtroTipo ? 'var(--cyan)' : 'var(--text-muted)', cursor:'pointer' }}>
          Tutti ({servers.length})
        </button>
        {TIPI.filter(t => servers.some(s => s.tipo === t)).map(t => (
          <button key={t} onClick={() => setFiltroTipo(t === filtroTipo ? '' : t)}
            style={{ fontSize:11, padding:'4px 10px', borderRadius:6, border:'1px solid var(--border)', background: filtroTipo===t ? 'var(--surface)' : 'transparent', color: TIPI_COLOR[t]||'var(--text-muted)', cursor:'pointer' }}>
            {t} ({servers.filter(s=>s.tipo===t).length})
          </button>
        ))}
      </div>

      {gruppi.map(g => (
        <div key={g.tipo} style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, fontWeight:700, color: TIPI_COLOR[g.tipo]||'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>
            {g.tipo}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {g.items.map(s => isMobile ? (
              <div key={s.id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'14px 16px', display:'flex', flexDirection:'column', gap:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:13, color:'var(--text)' }}>{s.nome}</div>
                    {s.note && <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:2 }}>{s.note}</div>}
                    {s.stato !== 'attivo' && <span style={{ fontSize:10, padding:'1px 6px', borderRadius:10, background:'var(--red-dim)', color:'var(--red)', marginTop:4, display:'inline-block' }}>{s.stato}</span>}
                  </div>
                  <div style={{ display:'flex', gap:4 }}>
                    <button onClick={() => openEdit(s)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:5, borderRadius:6, display:'flex' }}>
                      <Pencil size={13}/>
                    </button>
                    <button onClick={() => del(s.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:5, borderRadius:6, display:'flex' }}>
                      <Trash2 size={13}/>
                    </button>
                  </div>
                </div>
                {(s.host) && (
                  <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <span style={{ fontFamily:'monospace', fontSize:12, color:'var(--text-muted)' }}>{s.host}{s.porta ? `:${s.porta}` : ''}</span>
                    <CopyBtn value={s.host}/>
                  </div>
                )}
                {s.username && (
                  <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <span style={{ fontSize:12, color:'var(--text-muted)' }}>User: {s.username}</span>
                    <CopyBtn value={s.username}/>
                  </div>
                )}
                {s.password && (
                  <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <span style={{ fontFamily:'monospace', fontSize:12, color:'var(--text-muted)', letterSpacing: showPwd[s.id] ? 'normal' : '0.1em' }}>
                      {showPwd[s.id] ? s.password : '••••••••'}
                    </span>
                    <button onClick={() => togPwd(s.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:'2px 4px', display:'flex' }}>
                      {showPwd[s.id] ? <EyeOff size={12}/> : <Eye size={12}/>}
                    </button>
                    {showPwd[s.id] && <CopyBtn value={s.password}/>}
                  </div>
                )}
                {(s.tags||[]).length > 0 && (
                  <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                    {(s.tags||[]).map(t => (
                      <span key={t} style={{ fontSize:10, padding:'1px 6px', borderRadius:10, background:'var(--bg3)', color:'var(--text-muted)' }}>{t}</span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div key={s.id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'14px 18px', display:'grid', gridTemplateColumns:'1.2fr 1.5fr 1fr 1fr 1fr 80px', gap:12, alignItems:'center' }}>
                {/* Nome + stato */}
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:'var(--text)' }}>{s.nome}</div>
                  {s.note && <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:2 }}>{s.note}</div>}
                  {s.stato !== 'attivo' && <span style={{ fontSize:10, padding:'1px 6px', borderRadius:10, background:'var(--red-dim)', color:'var(--red)', marginTop:4, display:'inline-block' }}>{s.stato}</span>}
                </div>
                {/* Host */}
                <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                  <span style={{ fontFamily:'monospace', fontSize:12, color:'var(--text-muted)' }}>{s.host || '—'}{s.porta ? `:${s.porta}` : ''}</span>
                  <CopyBtn value={s.host}/>
                </div>
                {/* User */}
                <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                  <span style={{ fontSize:12, color:'var(--text-muted)' }}>{s.username || '—'}</span>
                  <CopyBtn value={s.username}/>
                </div>
                {/* Password */}
                <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                  <span style={{ fontFamily:'monospace', fontSize:12, color:'var(--text-muted)', letterSpacing: showPwd[s.id] ? 'normal' : '0.1em' }}>
                    {s.password ? (showPwd[s.id] ? s.password : '••••••••') : '—'}
                  </span>
                  {s.password && (
                    <button onClick={() => togPwd(s.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:'2px 4px', display:'flex' }}>
                      {showPwd[s.id] ? <EyeOff size={12}/> : <Eye size={12}/>}
                    </button>
                  )}
                  {showPwd[s.id] && <CopyBtn value={s.password}/>}
                </div>
                {/* Tags */}
                <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                  {(s.tags||[]).map(t => (
                    <span key={t} style={{ fontSize:10, padding:'1px 6px', borderRadius:10, background:'var(--bg3)', color:'var(--text-muted)' }}>{t}</span>
                  ))}
                </div>
                {/* Azioni */}
                <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                  <button onClick={() => openEdit(s)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:5, borderRadius:6, display:'flex' }}
                    onMouseEnter={e=>e.currentTarget.style.color='var(--cyan)'} onMouseLeave={e=>e.currentTarget.style.color='var(--text-dim)'}>
                    <Pencil size={13}/>
                  </button>
                  <button onClick={() => del(s.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:5, borderRadius:6, display:'flex' }}
                    onMouseEnter={e=>e.currentTarget.style.color='var(--red)'} onMouseLeave={e=>e.currentTarget.style.color='var(--text-dim)'}>
                    <Trash2 size={13}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div style={{ textAlign:'center', padding:60, color:'var(--text-dim)', fontSize:14 }}>
          Nessun server configurato.<br/>
          <span style={{ fontSize:12 }}>Clicca "+ Nuovo server" per aggiungere.</span>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => { if(e.target===e.currentTarget) setModalOpen(false); }}>
          <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:16, padding:'24px 20px', width:'min(560px, calc(100vw - 24px))', maxHeight:'90dvh', overflowY:'auto' }}>
            <h2 style={{ fontSize:16, fontWeight:700, margin:'0 0 20px', color:'var(--text)' }}>{editItem ? 'Modifica server' : 'Nuovo server'}</h2>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <input placeholder="Nome *" value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} className="eden-input"/>
              <select value={form.tipo} onChange={e=>setForm(f=>({...f,tipo:e.target.value}))} className="eden-input">
                {TIPI.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10, marginBottom:10 }}>
              <input placeholder="Host / IP" value={form.host} onChange={e=>setForm(f=>({...f,host:e.target.value}))} className="eden-input"/>
              <input placeholder="Porta" type="number" value={form.porta} onChange={e=>setForm(f=>({...f,porta:e.target.value}))} className="eden-input"/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <input placeholder="Username" value={form.username} onChange={e=>setForm(f=>({...f,username:e.target.value}))} className="eden-input"/>
              <input placeholder="Password" type="text" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} className="eden-input"/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10, marginBottom:10 }}>
              <input placeholder="Note" value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} className="eden-input"/>
              <select value={form.stato} onChange={e=>setForm(f=>({...f,stato:e.target.value}))} className="eden-input">
                {['attivo','manutenzione','dismesso'].map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <input placeholder="Tag (separati da virgola)" value={(form.tags||[]).join(', ')}
              onChange={e=>setForm(f=>({...f,tags:e.target.value.split(',').map(t=>t.trim()).filter(Boolean)}))}
              className="eden-input" style={{ marginBottom:16, width:'100%', boxSizing:'border-box' }}/>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={submit} className="eden-btn-primary">Salva</button>
              <button onClick={() => setModalOpen(false)} className="eden-btn-ghost">Annulla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
